import axios from "axios";
import React, { createContext, ReactNode, useContext, useState } from "react";
import { BASEURL } from "../constants/baseUrl";

export type MediaItem = {
  type: "image" | "video";
  url?: string;   // pour vidéo ou images déjà en ligne
  file?: File;    // pour upload
  position?: number;
};

export type WorkItem = {
  id?: number;
  title: string;
  description?: string;
  category: "film" | "dessin" | "illustration" | "animation";
  image_file?: File;             // image principale pour upload
  image_url?: string;            // URL si déjà en ligne
  media_files?: MediaItem[];     // images secondaires + vidéos
  video_url?: string;            // URL vidéo (optionnelle)
  created_at?: string | number | Date;
};

type WorkContextType = {
  works: WorkItem[];
  selectedWork: WorkItem | null;
  loading: boolean;

  // Actions
  createWork: (work: WorkItem, token: string) => Promise<void>;
  updateWork: (id: number, work: WorkItem, token: string) => Promise<void>;
  deleteWork: (id: number, token: string) => Promise<void>;

  selectWork: (work: WorkItem | null) => void;
  clearWorks: () => void;

  fetchWorks: () => Promise<void>;
  fetchWorkById: (id: number) => Promise<void>;
  fetchWorksByCategory: (category: WorkItem["category"]) => Promise<void>;
};

export const WorkContext = createContext<WorkContextType>({
  works: [],
  selectedWork: null,
  loading: false,
  createWork: async () => {},
  updateWork: async () => {},
  deleteWork: async () => {},
  selectWork: () => {},
  clearWorks: () => {},
  fetchWorks: async () => {},
  fetchWorkById: async () => {},
  fetchWorksByCategory: async () => {},
});

type Props = { children: ReactNode };

export const WorkProvider = ({ children }: Props) => {
  const [works, setWorks] = useState<WorkItem[]>([]);
  const [selectedWork, setSelectedWork] = useState<WorkItem | null>(null);
  const [loading, setLoading] = useState(false);

  // ---------------- Création d'un projet ----------------
  const createWork = async (work: WorkItem, token: string) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("title", work.title);
      formData.append("category", work.category);
      if (work.description) formData.append("description", work.description);
      if (work.image_file) formData.append("image", work.image_file);
      if (work.video_url) formData.append("video_url", work.video_url);

      work.media_files
        ?.filter((m) => m.type === "image" && m.file)
        .forEach((m) => formData.append("media", m.file!));

      const res = await axios.post(`${BASEURL}/api/admin/works`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.success) {
        setWorks((prev) => [...prev, { ...work, id: res.data.workId }]);
      } else {
        console.error("Server returned success=false");
      }
    } catch (err) {
      console.error("Error creating work:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateWork = async (id: number, work: WorkItem, token: string) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("title", work.title);
      formData.append("category", work.category);

      if (work.description) formData.append("description", work.description);
      if (work.image_file) formData.append("image", work.image_file);
      if (work.video_url) formData.append("video_url", work.video_url);

      work.media_files
        ?.filter((m) => m.type === "image" && m.file)
        .forEach((m) => formData.append("media", m.file!));

      await axios.put(`${BASEURL}/api/admin/works/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // mise à jour locale
      setWorks((prev) =>
        prev.map((w) => (w.id === id ? { ...work, id } : w))
      );

      setSelectedWork({ ...work, id });
    } catch (err) {
      console.error("Error updating work:", err);
    } finally {
      setLoading(false);
    }
  };

  const deleteWork = async (id: number, token: string) => {
    try {
      setLoading(true);

      await axios.delete(`${BASEURL}/api/admin/works/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setWorks((prev) => prev.filter((w) => w.id !== id));
      setSelectedWork(null);
    } catch (err) {
      console.error("Error deleting work:", err);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Sélection / focus sur projet ----------------
  const selectWork = (work: WorkItem | null) => {
    setSelectedWork(work);
    console.log("Selected work:", work?.media_files);
  };

  // ---------------- Réinitialisation ----------------
  const clearWorks = () => {
    setWorks([]);
    setSelectedWork(null);
  };

  // ---------------- Fetch depuis API ----------------

  // Récupérer tous les travaux
  const fetchWorks = async () => {
    setLoading(true);
    try {
      const res = await axios.get<WorkItem[]>(`${BASEURL}/api/works/works`);
      setWorks(res.data);
    } catch (err) {
      console.error("Error fetching works:", err);
    } finally {
      setLoading(false);
    }
  };

  // Récupérer un projet spécifique
const fetchWorkById = async (id: number) => {
  setLoading(true);
  try {
    const res = await axios.get(`${BASEURL}/api/works/works/${id}`);
    
    // Transformation pour correspondre à WorkItem
    const workData: WorkItem = {
      ...res.data,
      media_files: res.data.media || [], // le back renvoie 'media'
    };

    setSelectedWork(workData);
    console.log("Fetched work by ID:", workData);
  } catch (err) {
    console.error("Error fetching work by ID:", err);
  } finally {
    setLoading(false);
  }
};


  // Récupérer les travaux par catégorie
  const fetchWorksByCategory = async (category: WorkItem["category"]) => {
    setLoading(true);
    try {
      const res = await axios.get<WorkItem[]>(`${BASEURL}/api/works/works/category/${category}`);
      setWorks(res.data);
    } catch (err) {
      console.error("Error fetching works by category:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkContext.Provider
      value={{
        works,
        selectedWork,
        loading,
        createWork,
        updateWork,
        deleteWork,
        selectWork,
        clearWorks,
        fetchWorks,
        fetchWorkById,
        fetchWorksByCategory,
      }}
    >
      {children}
    </WorkContext.Provider>
  );
};

export const useWorks = () => useContext(WorkContext);
